import type { ReactNode } from 'react';
import type { Experiment } from 'types';
import type { StackedField } from '../../../ConsolePanel';
import { useState } from 'react';
import { toast } from 'sonner';
import { GRID_TABLE_ROW, GRID_TABLE_ROWGROUP, GridTable, StackedRow } from '../../../ConsolePanel';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { useExperiment } from '@/state/experiments/useExperiment';
import { apiErrorMessage } from '@/state/api-error';
import { deployRefusal } from 'helpers/deploy-rule';
import { formatNumber, formatPercentage } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import { ALL_CURRENCIES, revenueNeedsCurrency } from './control-options';
import { TooltipProvider } from '@/components/ui/tooltip';
import { MetricLabel } from './MetricLabel';
import { Eye } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Typed rather than `any`. The manager being untyped is what let the significance
 * read-out look up significanceData[currency][goal].frequentist[correction] against
 * a payload that had no `frequentist` level, and fail silently at runtime instead of
 * at compile time.
 */
type StatsManager = ReturnType<typeof import('@/state/experiments/useExperimentStats').useExperimentStats>;

type StatisticsTableProps = {
  experiment: Experiment;
  manager: StatsManager;
  /** Called once a variant has been deployed, after the confirmation has closed. */
  onDeployed: () => Promise<void>;
};

const CHIP_CLASS = 'rounded-pill border px-2 py-0.5 font-mono text-nano uppercase tracking-[0.1em]';

/**
 * Two weights of cell, matching `SignificanceTable`.
 *
 * `RESULT` is the outcome the reader came for — the difference and the interval —
 * and `SUPPORT` is the traffic that produced it: ids, weight, sessions, the raw
 * conversion/revenue count. All of it was one 12px `--text` before, which left the
 * conclusion no louder than its own inputs. Both stay `num`, so the columns still
 * align down the page.
 */
const RESULT_CELL = 'text-body font-medium';
const SUPPORT_CELL = 'text-dim';

type CellProps = { children: ReactNode; className?: string; title?: string };

/** Every numeric cell is `num`, so the columns line up down the page. */
const Cell = ({ children, className, title }: CellProps) => (
  <div role="cell" title={title} className={cn('num text-foreground', className)}>
    {children}
  </div>
);

const Head = ({ children, className }: CellProps) => (
  <div role="columnheader" className={cn('micro-label', className)}>
    {children}
  </div>
);

export const StatisticsTable = ({ experiment, manager, onDeployed }: StatisticsTableProps) => {
  const [wantsToDeploy, setWantsToDeploy] = useState<any>(null);
  const {
    variants,
    statistics,
    totalSessions,
    goal,
    goalLabels,
    significance,
    evaluationMethod,
    currency,
    currencies,
  } = manager;
  const isBayesian = evaluationMethod === 'bayesian';
  const goalLabel = goalLabels?.[goal] ?? goal;
  const { deployExperiment } = useExperiment(experiment.id);

  /**
   * Revenue needs one currency. Conversions do not.
   *
   * A count is a count whatever it was paid in, so "All currencies" is the right
   * default and the right broadest view. Revenue cannot work that way, adding SEK
   * onto EUR produces a number that means nothing, so under All currencies the
   * revenue figures cover only the traffic recorded without a currency, which is a
   * subset masquerading as a total. Say so rather than show it.
   *
   * A site that never sends a currency has all of its revenue under 'nill', and for
   * that site 'nill' is its only currency rather than an aggregate, so revenue is
   * shown normally.
   */
  const currencyList: string[] = currencies ?? [];
  const needsCurrency = revenueNeedsCurrency(goal, currency, currencyList);

  if (!statistics) {
    return (
      <div className="border-line bg-panel2 rounded-card border px-4 py-10 text-center">
        <p className="supporting">No statistics data available yet.</p>
      </div>
    );
  }

  if (needsCurrency) {
    const named = currencyList.filter((c) => c !== ALL_CURRENCIES).map((c) => c.toUpperCase());
    return (
      <div className="border-warn-line bg-panel2 rounded-card space-y-2 border border-l-2 px-4 py-4">
        <p className="micro-label text-warn">Pick a currency to see revenue</p>
        <p className="supporting max-w-3xl">
          This experiment recorded revenue in {named.length} currencies ({named.join(', ')}). They cannot be added
          together, so there is no all-currency revenue figure. Choose one above, or switch the objective to{' '}
          {goalLabels?.Conversions ?? 'Conversions'}, which counts the same across every currency.
        </p>
      </div>
    );
  }

  const onDeployConfirm = async () => {
    if (!wantsToDeploy) return;
    try {
      await deployExperiment(wantsToDeploy.id);
    } catch (error) {
      // A 409 says why (already deployed, never started). The dialog closes so the refetched
      // experiment shows what happened instead.
      setWantsToDeploy(null);
      toast.error(apiErrorMessage(error, 'The variant could not be deployed'));
      return;
    }
    setWantsToDeploy(null);
    // Deploying a winner is also the end of the test, so it is offered the same debrief as a
    // manual End. After the confirmation has closed, so the wizard does not open under it.
    await onDeployed();
  };

  const deployed = variants?.find((variant) => variant.deployed);
  // The server's rule, so the button is never offered for a deploy that would answer 409. Its
  // reason is the disabled button's title (on a wrapper: a disabled button takes no hover).
  const deployBlocked = deployRefusal({
    status: experiment.status,
    startsAt: experiment.startsAt,
    variants: experiment.variants ?? [],
  });

  /**
   * The deployed variant's aggregates, or zeros.
   *
   * `statistics[id]?.revenue / 100` evaluates to NaN when the row is absent, the
   * optional chain guards the property access but not the arithmetic after it, and
   * a deployed variant with no traffic yet is exactly that case.
   */
  const deployedStats = {
    sessions: 0,
    revenue: 0,
    conversions: 0,
    revenueAverage: 0,
    average: 0,
    ...(deployed ? statistics[deployed.id] : undefined),
  };

  const data = variants
    ?.filter((variant) => !variant.deployed)
    // Stats are resolved explicitly rather than spread. A variant with no traffic yet
    // has no stats row, and every read below is arithmetic: `undefined / 100` is NaN,
    // which formatNumber then renders as garbage. Spreading also collided with
    // Variant's own `sessions` column, making the winning source ambiguous.
    .map((variant) => {
      const stats = statistics[variant.id];
      return {
        ...variant,
        sessions: stats?.sessions ?? 0,
        conversions: stats?.conversions ?? 0,
        revenue: stats?.revenue ?? 0,
        average: stats?.average ?? 0,
        revenueAverage: stats?.revenueAverage ?? 0,
        difference: stats?.difference ?? null,
        improvement: stats?.improvement ?? null,
        revenueDifference: stats?.revenueDifference ?? null,
        revenueImprovement: stats?.revenueImprovement ?? null,
      };
    })
    .sort((a, b) => {
      const aIsControl = experiment.controlVariantId ? a.id === experiment.controlVariantId : a.name === 'Original';
      const bIsControl = experiment.controlVariantId ? b.id === experiment.controlVariantId : b.name === 'Original';
      if (aIsControl) return -1;
      if (bIsControl) return 1;
      return (a.createdAt ?? 0) - (b.createdAt ?? 0);
    });

  const controlRow = data?.find((v) =>
    experiment.controlVariantId ? v.id === experiment.controlVariantId : v.name === 'Original',
  );
  const controlAverage = goal === 'Revenue' ? controlRow?.revenueAverage : controlRow?.average;

  /**
   * Derived once, rendered twice — as a grid row on a desktop and as a stack of
   * label/value pairs on a phone.
   *
   * The grid's floor is 1140px, so at 375px a reader has to swipe the variant name
   * off screen to reach the difference and the interval, which is exactly the
   * comparison the page exists for. Two renderers over one array is what stops the
   * narrow shape from drifting away from the wide one.
   */
  const readings = (data ?? []).map((item) => {
    const { controlVariantId } = experiment;
    const isControl = controlVariantId ? item.id === controlVariantId : item.name === 'Original';
    const deployedCount = (deployed ? deployed.visitorCount?.count : 0) ?? 0;
    const realWeight = totalSessions ? (item.sessions / (totalSessions - deployedCount)) * 100 : 0;

    const difference = goal === 'Revenue' ? item.revenueDifference : item.difference;
    const improvement = goal === 'Revenue' ? item.revenueImprovement : item.improvement;
    const rowAverage = goal === 'Revenue' ? item.revenueAverage : item.average;
    const averageImprovement =
      !isControl && controlAverage ? ((rowAverage - controlAverage) / controlAverage) * 100 : null;

    const sig = significance?.find((s) => s.id === item.id);
    const ci = sig?.confidenceInterval ?? sig?.credibleInterval;
    const prob = sig?.probabilityOfBeingBest;

    /**
     * The row that carries the conclusion. A variant only leads if the significance
     * read-out says the result is real AND it is ahead — a significant loss is not a
     * winner, and flagging it as one would be worse than flagging nothing at all.
     */
    const leads = !isControl && !!sig?.significant && difference != null && difference > 0;

    const fields: StackedField[] = [
      {
        key: 'weight',
        label: <MetricLabel metric="weight">Weight</MetricLabel>,
        className: SUPPORT_CELL,
        value:
          item.name === 'Original'
            ? `${formatPercentage((item.weight || 0) / 100)}`
            : `${formatPercentage((item.weight || 0) / 100)} (${formatPercentage(realWeight)})`,
      },
      {
        key: 'sessions',
        label: <MetricLabel metric="sessions">Sessions</MetricLabel>,
        className: SUPPORT_CELL,
        value: formatNumber(item.sessions),
      },
      {
        key: 'goal',
        label: (
          <MetricLabel metric={goal === 'Revenue' ? 'revenue' : 'conversions'}>
            {goal === 'Revenue' ? 'Revenue' : goalLabel}
          </MetricLabel>
        ),
        value: (
          <span className="inline-flex items-center gap-2">
            <span className={SUPPORT_CELL}>
              {goal === 'Revenue' ? formatNumber((item.revenue ?? 0) / 100) : formatNumber(item.conversions ?? 0)}
            </span>
            <ImprovementChip value={improvement} />
          </span>
        ),
      },
      {
        key: 'average',
        label: <MetricLabel metric={goal === 'Revenue' ? 'averageRevenue' : 'average'}>Average</MetricLabel>,
        value: (
          <span className="inline-flex items-center gap-2">
            <span>
              {goal === 'Revenue' ? formatNumber(item.revenueAverage ?? 0) : formatPercentage(item.average ?? 0)}
            </span>
            <ImprovementChip value={averageImprovement} />
          </span>
        ),
      },
      {
        key: 'difference',
        label: <MetricLabel metric="difference">Difference</MetricLabel>,
        className: cn(RESULT_CELL, difference != null && (difference > 0 ? 'text-ok' : 'text-danger')),
        value:
          difference != null ? (goal === 'Revenue' ? formatNumber(difference / 100) : formatNumber(difference)) : '-',
      },
      {
        key: 'ci',
        label: <MetricLabel metric="wilsonCi">CI (95%)</MetricLabel>,
        className: RESULT_CELL,
        value: !ci || isControl ? '-' : `${formatPercentage(ci.lower * 100)} - ${formatPercentage(ci.upper * 100)}`,
      },
      ...(isBayesian
        ? [
            {
              key: 'probabilityBest',
              label: <MetricLabel metric="probabilityBest">Prob. best</MetricLabel>,
              value:
                prob === undefined || isControl ? (
                  '-'
                ) : (
                  // The Bayesian verdict, so it reads a size up like the frequentist
                  // "Significant" chip does.
                  <span
                    className={cn(
                      'rounded-pill border px-2 py-0.5 font-mono text-micro font-medium uppercase tracking-[0.1em]',
                      probabilityTone(prob),
                    )}
                  >
                    {formatPercentage(prob * 100)}
                  </span>
                ),
            },
          ]
        : []),
    ];

    return { item, isControl, leads, fields };
  });

  // Every row carries the same reading columns, so the first one's labels are the head
  // row. Empty when there are no variants, which is also when there are no rows.
  const headFields = readings[0]?.fields ?? [];
  // The tour points at one Deploy button: the first row that has one (the Original has none).
  const firstDeployableId = readings.find(({ item }) => item.name !== 'Original')?.item.id;

  // The name column takes the spare width; every reading column keeps a floor so the
  // grid scrolls sideways inside the card instead of crushing the numbers together.
  const template = [
    '64px',
    'minmax(180px,1fr)',
    'minmax(130px,auto)',
    'minmax(100px,auto)',
    'minmax(150px,auto)',
    'minmax(150px,auto)',
    // Difference and CI carry 13px mono rather than 12px now, so their floors go up
    // with them — the point of a floor is that the number never wraps.
    'minmax(120px,auto)',
    'minmax(176px,auto)',
    ...(isBayesian ? ['minmax(110px,auto)'] : []),
    ...(!deployed && experiment.editorUrl ? ['56px'] : []),
    ...(!deployed ? ['92px'] : []),
  ].join(' ');

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {deployed && (
          // sel, not ok: the deployed variant is the one that has been chosen, which is
          // a different statement from "this variant won".
          <div className="border-sel-line bg-sel-bg rounded-card flex flex-wrap items-end gap-x-8 gap-y-3 border px-4 py-3">
            <div className="space-y-0.5">
              <span className="micro-label text-sel">Deployed variant</span>
              <div className="flex items-baseline gap-2">
                <span className="text-foreground text-body">{deployed.name}</span>
                <span className="num text-dim">#{deployed.id}</span>
              </div>
            </div>

            <Stat label={<MetricLabel metric="sessions">Sessions</MetricLabel>}>
              {formatNumber(deployedStats.sessions)}
            </Stat>
            <Stat
              label={
                <MetricLabel metric={goal === 'Revenue' ? 'revenue' : 'conversions'}>
                  {goal === 'Revenue' ? 'Revenue' : goalLabel}
                </MetricLabel>
              }
            >
              {goal === 'Revenue' ? formatNumber(deployedStats.revenue / 100) : formatNumber(deployedStats.conversions)}
            </Stat>
            <Stat label={<MetricLabel metric={goal === 'Revenue' ? 'averageRevenue' : 'average'}>Average</MetricLabel>}>
              {goal === 'Revenue'
                ? formatNumber(deployedStats.revenueAverage)
                : formatPercentage(deployedStats.average)}
            </Stat>
          </div>
        )}

        <div className="border-line bg-panel2 rounded-card border">
          <div className="border-line border-b px-4 py-3">
            <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">
              {deployed ? 'Results' : 'Statistics'}
            </h3>
            <p className="supporting">Analyze performance metrics and results from your experiment.</p>
          </div>

          {/* Below `md` every variant becomes its own block, with the row's controls
              under the readings rather than in columns off the right edge. */}
          <div className="flex flex-col gap-2 p-3 md:hidden">
            {readings.map(({ item, isControl, leads, fields }) => (
              <StackedRow
                key={item.id}
                className={leads ? 'border-l-ok' : undefined}
                heading={
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-foreground min-w-0 truncate text-body" title={item.name}>
                      {item.name}
                    </span>
                    <span className="num text-dim shrink-0">#{item.id}</span>
                    {isControl && <span className={cn(CHIP_CLASS, 'border-line text-dim')}>Control</span>}
                    {leads && <span className={cn(CHIP_CLASS, 'border-ok-line text-ok')}>Leading</span>}
                  </div>
                }
                fields={fields}
              >
                {!deployed && (item.name !== 'Original' || experiment.editorUrl) && (
                  <div className="border-line mt-2.5 flex flex-wrap items-center gap-2 border-t pt-2.5">
                    {experiment.editorUrl && <ForceVariantMenu name={item.name} />}
                    {item.name !== 'Original' && (
                      <span className="inline-flex flex-1" title={deployBlocked ?? undefined}>
                        <Button
                          size="sm"
                          className="h-9 w-full rounded-lg px-2.5 font-mono text-nano uppercase tracking-[0.1em]"
                          disabled={!!deployBlocked}
                          onClick={() => setWantsToDeploy(item)}
                          data-tour={item.id === firstDeployableId ? 'results-deploy' : undefined}
                        >
                          Deploy
                        </Button>
                      </span>
                    )}
                  </div>
                )}
              </StackedRow>
            ))}
          </div>

          <GridTable label="Variant statistics" minWidth={1140} template={template}>
            {/* Heads come off the same field list the cells do, so a column cannot end
                up labelled one thing on a desktop and another on a phone. */}
            <div
              role="row"
              className={cn(GRID_TABLE_ROW, 'border-b-line border-l-2 border-l-transparent items-center border-b py-2')}
            >
              <Head>ID</Head>
              <Head>Name</Head>
              {headFields.map((field) => (
                <Head key={field.key}>{field.label}</Head>
              ))}
              {!deployed && experiment.editorUrl && <Head>View</Head>}
              {!deployed && <Head>Deploy</Head>}
            </div>

            <div role="rowgroup" className={GRID_TABLE_ROWGROUP}>
              {readings.map(({ item, isControl, leads, fields }) => (
                <div
                  key={item.id}
                  role="row"
                  className={cn(
                    GRID_TABLE_ROW,
                    'border-b-line hover:bg-hover items-center border-b py-2.5',
                    'transition-colors duration-150 last:border-b-0',
                    // Every row carries the same 2px left border, transparent when
                    // the row does not lead, so no row shifts out of line with the
                    // head when one of them gains an edge.
                    'border-l-2',
                    leads ? 'border-l-ok' : 'border-l-transparent',
                  )}
                >
                  <Cell className={SUPPORT_CELL}>{item.id}</Cell>
                  <Cell className="font-sans text-body">
                    <div className="flex items-center gap-2">
                      <span className="truncate" title={item.name}>
                        {item.name}
                      </span>
                      {isControl && <span className={cn(CHIP_CLASS, 'border-line text-dim')}>Control</span>}
                      {leads && <span className={cn(CHIP_CLASS, 'border-ok-line text-ok')}>Leading</span>}
                    </div>
                  </Cell>
                  {fields.map((field) => (
                    <Cell key={field.key} className={field.className}>
                      {field.value}
                    </Cell>
                  ))}
                  {!deployed && experiment.editorUrl && (
                    <Cell>
                      <ForceVariantMenu name={item.name} />
                    </Cell>
                  )}
                  {!deployed && (
                    <Cell>
                      {item.name !== 'Original' && (
                        <span className="inline-flex" title={deployBlocked ?? undefined}>
                          <Button
                            size="sm"
                            className="h-7 rounded-lg px-2.5 font-mono text-nano uppercase tracking-[0.1em]"
                            disabled={!!deployBlocked}
                            onClick={() => setWantsToDeploy(item)}
                            data-tour={item.id === firstDeployableId ? 'results-deploy' : undefined}
                          >
                            Deploy
                          </Button>
                        </span>
                      )}
                    </Cell>
                  )}
                </div>
              ))}
            </div>
          </GridTable>
        </div>

        <ConfirmDialog
          title={`Deploy Variant "${wantsToDeploy?.name}"?`}
          open={!!wantsToDeploy}
          onConfirm={onDeployConfirm}
          onCancel={() => setWantsToDeploy(null)}
        >
          Are you sure you want to deploy this variant?
        </ConfirmDialog>
      </div>
    </TooltipProvider>
  );
};

type ForceVariantMenuProps = { name: string };

/**
 * Extracted so the wide and narrow layouts share one menu rather than two copies.
 * size-9 on a phone: a 28px icon button is under the 36px a thumb can rely on.
 */
const ForceVariantMenu = ({ name }: ForceVariantMenuProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Force variant ${name} on the site`}
        title={`Force variant ${name} on the site`}
        className="text-dim hover:bg-hover hover:text-foreground size-9 rounded-lg p-0 md:size-7"
      >
        <Eye className="size-3.5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent>
      <DropdownMenuLabel>Force Variant</DropdownMenuLabel>
      <DropdownMenuItem>Only this Variant</DropdownMenuItem>
      <DropdownMenuItem>Keep other Variants</DropdownMenuItem>
      <DropdownMenuItem>Reset</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

type StatProps = { label: ReactNode; children: ReactNode };

const Stat = ({ label, children }: StatProps) => (
  <div className="space-y-0.5">
    <div className="micro-label">{label}</div>
    <div className="num text-foreground">{children}</div>
  </div>
);

const probabilityTone = (probability: number) => {
  if (probability >= 0.9) return 'border-ok-line text-ok';
  if (probability >= 0.6) return 'border-warn-line text-warn';
  return 'border-line text-dim';
};

type ImprovementChipProps = { value: number | null | undefined };

/**
 * A delta, so the fill IS the message — solid, unlike every status and verdict chip
 * in this file, which stay outline-only because they already say "Control" or
 * "Significant" in words.
 *
 * The solid pair is not `--ok`/`--danger`: those are tuned to read as text on a
 * panel, and white on the light `--ok` is 3.74:1, which fails AA at this size.
 *
 * Exactly zero stays neutral. Painting `00.00%` green would claim an improvement
 * that the number explicitly denies.
 */
const DELTA_CHIP_CLASS = 'shrink-0 rounded-pill px-1.5 py-px font-mono text-nano font-medium leading-[1.6]';

const ImprovementChip = ({ value }: ImprovementChipProps) => {
  if (value === undefined || value === null) return null;

  if (value === 0) {
    return <span className={cn(DELTA_CHIP_CLASS, 'border-line text-dim border')}>{formatPercentage(value)}</span>;
  }

  return (
    <span
      className={cn(
        DELTA_CHIP_CLASS,
        value > 0 ? 'bg-ok-solid text-ok-solid-fg' : 'bg-danger-solid text-danger-solid-fg',
      )}
    >
      {formatPercentage(value)}
    </span>
  );
};
